// VideoTrimmer.js
import React, { useState, useRef } from 'react';
// Import FFmpeg from '@ffmpeg/ffmpeg' and fetchFile from '@ffmpeg/util'
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';
import { Card, CardHeader, CardBody } from '../card/card.jsx';
import CONFIG from '../../config/config.js';

const VideoTrimmer = ({ label, setFileName }) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoBlobUrl, setVideoBlobUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isTrimming, setIsTrimming] = useState(false);

  // Create an instance of FFmpeg using its static method createFFmpeg.
  const ffmpegRef = useRef(new FFmpeg());
  const ffmpeg = ffmpegRef.current
  const videoRef = useRef(null);

  // Load video from backend.
  const handleLoadVideo = async () => {
    if (!youtubeUrl) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${CONFIG.API_URL}/utils/yt-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: youtubeUrl })
      });
      if (!response.ok) {
        throw new Error('Failed to download video.');
      }
      const data = await response.json();
      // Construct the video URL from the returned filename.
      const videoUrl = `${CONFIG.API_BASE}/downloads/${data.filename}`;
      setVideoBlobUrl(videoUrl);

      // Fetch the video file for ffmpeg trimming.
      const videoResponse = await fetch(videoUrl);
      if (!videoResponse.ok) {
        throw new Error('Failed to fetch stored video file.');
      }
      const videoBlob = await videoResponse.blob();
      const file = new File([videoBlob], data.filename, { type: videoBlob.type });
      setVideoFile(file);
    } catch (err) {
      console.error('Error loading video:', err);
    }
    setIsLoading(false);
  };

  // When video metadata loads, record its full duration.
  const handleVideoLoadedMetadata = (e) => {
    const duration = e.target.duration;
    setVideoDuration(duration);
    setEndTime(duration);
  };

  // Update start and end times when slider moves.
  const onSliderChange = (values) => {
    const [newStart, newEnd] = values;
    setStartTime(newStart);
    setEndTime(newEnd);
    if (videoRef.current) {
      videoRef.current.currentTime = newStart;
    }
  };

  // Use ffmpeg.wasm to trim the video clip, then upload the trimmed file.
  const handleTrimAndUpload = async () => {
    if (!videoFile || startTime >= endTime) return;
    setIsTrimming(true);
    try {
      if (!ffmpeg.isLoaded()) {
        await ffmpeg.load();
      }
      // Write the input video file to the virtual FS.
      ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(videoFile));
      // Run the ffmpeg command to trim the video.
      await ffmpeg.run(
        '-i', 'input.mp4',
        '-ss', `${startTime}`,
        '-to', `${endTime}`,
        '-c', 'copy',
        'output.mp4'
      );
      // Read the output file from the FS.
      const data = ffmpeg.FS('readFile', 'output.mp4');
      const trimmedBlob = new Blob([data.buffer], { type: 'video/mp4' });
      const formData = new FormData();
      formData.append('video', trimmedBlob, 'trimmed_video.mp4');

      const uploadResponse = await fetch(`${CONFIG.API_URL}/quiz/upload`, {
        method: 'POST',
        body: formData
      });
      if (!uploadResponse.ok) {
        throw new Error('Upload failed.');
      }
      const result = await uploadResponse.json();
      if (result.filename) {
        setFileName(result.filename);
      }
    } catch (err) {
      console.error('Error trimming or uploading video:', err);
    }
    setIsTrimming(false);
  };

  return (
    <Card>
      <CardHeader>
        <h5>{label}</h5>
      </CardHeader>
      <CardBody>
        <div style={{ marginBottom: '1rem' }} className="row">
          <label>YouTube Video URL:</label>
            <input
              type="text"
              placeholder="Enter YouTube URL"
              className="form-control mb-1 flex-grow-1"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
            />
            <button
              onClick={handleLoadVideo}
              disabled={isLoading || !youtubeUrl}
              style={{ minWidth: "300px", minHeight: "38px" }}
              className="btn btn-success"
            >
              {isLoading ? 'Loading...' : 'Load Video'}
            </button>
        </div>
        {videoBlobUrl && (
          <div>
            <video
              ref={videoRef}
              src={videoBlobUrl}
              controls
              onLoadedMetadata={handleVideoLoadedMetadata}
              style={{
                width: '100%',
                maxWidth: '600px',
                display: 'block',
                marginBottom: '1rem'
              }}
            ></video>
            {videoDuration > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <p>
                  Trim Range: {startTime.toFixed(1)}s – {endTime.toFixed(1)}s (Total:{' '}
                  {videoDuration.toFixed(1)}s)
                </p>
                <Slider.Range
                  min={0}
                  max={videoDuration}
                  step={0.1}
                  value={[startTime, endTime]}
                  onChange={onSliderChange}
                  railStyle={{ backgroundColor: '#ddd', height: 10 }}
                  trackStyle={[{ backgroundColor: '#007bff', height: 10 }]}
                  handleStyle={[
                    {
                      borderColor: '#007bff',
                      height: 20,
                      width: 20,
                      marginLeft: -10,
                      marginTop: -5,
                      backgroundColor: '#fff'
                    },
                    {
                      borderColor: '#007bff',
                      height: 20,
                      width: 20,
                      marginLeft: -10,
                      marginTop: -5,
                      backgroundColor: '#fff'
                    }
                  ]}
                />
              </div>
            )}
            <button
              onClick={handleTrimAndUpload}
              disabled={isTrimming || startTime >= endTime}
              className="btn btn-success flex-grow-1"
              >
              {isTrimming ? 'Trimming & Uploading...' : 'Trim and Upload'}
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default VideoTrimmer;
